import React from 'react';
import { Html } from '@react-three/drei';
import * as THREE from 'three';
import { darkenColor } from './utils';

/**
 * ALL tent-like exhibitor structures share almost identical geometry —
 * a floor, three walls, corner poles and a roof. Only the roof shape,
 * colour palette and small trim details differ. Each function returns
 * a pure JSX tree of `<mesh>` elements (no state) and takes the same
 * `props` bag: `{ w, d, h, baseColor, roofColor, emissive }`.
 */

// ---- Standard 20x20 Canopy Tent (uniform booth model) -------------
// Open-sided pop-up canopy tent used for every exhibitor booth.
// 4 corner poles, a pyramid fabric roof and a small trim skirt.
export function CanopyTent({ w, d, h, baseColor, roofColor, emissive }) {
  const poleH = h;                    // pole (leg) height
  const roofPeak = h * 0.55;          // how high the peak rises above the poles
  const roofBase = h + 0.02;          // where roof sits (just above poles)
  return (
    <group>
      {/* Floor pad */}
      <mesh position={[0, 0.05, 0]} receiveShadow>
        <boxGeometry args={[w, 0.1, d]} />
        <meshStandardMaterial color="#3d3d3d" roughness={0.9} />
      </mesh>

      {/* 4 corner poles */}
      {[[-1, -1], [1, -1], [-1, 1], [1, 1]].map(([x, z]) => (
        <mesh key={`pole-${x}-${z}`} position={[(x * w) / 2, poleH / 2, (z * d) / 2]} castShadow>
          <cylinderGeometry args={[0.08, 0.08, poleH, 8]} />
          <meshStandardMaterial color="#2c3e50" metalness={0.6} roughness={0.4} />
        </mesh>
      ))}

      {/* Pyramid fabric roof (4-sided cone) */}
      <mesh position={[0, roofBase + roofPeak / 2, 0]} rotation={[0, Math.PI / 4, 0]} castShadow>
        <coneGeometry args={[Math.max(w, d) * 0.72, roofPeak, 4]} />
        <meshStandardMaterial
          color={roofColor}
          emissive={baseColor}
          emissiveIntensity={emissive * 0.5}
          roughness={0.6}
          side={THREE.DoubleSide}
        />
      </mesh>

      {/* Roof trim skirt (short valance around the eaves) */}
      <mesh position={[0, roofBase, 0]} castShadow>
        <boxGeometry args={[w + 0.15, 0.25, d + 0.15]} />
        <meshStandardMaterial
          color={baseColor}
          emissive={baseColor}
          emissiveIntensity={emissive * 0.4}
        />
      </mesh>

      {/* Peak cap */}
      <mesh position={[0, roofBase + roofPeak + 0.08, 0]}>
        <sphereGeometry args={[0.16, 8, 8]} />
        <meshStandardMaterial color="#4b5563" metalness={0.7} />
      </mesh>
    </group>
  );
}

// ---- 01. Main Tent (khemah_utama) ---------------------------------
export function KhemahUtama({ w, d, h, baseColor, roofColor, emissive }) {
  return (
    <group>
      <mesh position={[0, 0.1, 0]} receiveShadow>
        <boxGeometry args={[w, 0.2, d]} />
        <meshStandardMaterial color="#8b7355" roughness={0.8} />
      </mesh>
      <mesh position={[0, h/2, -d/2]} castShadow>
        <boxGeometry args={[w, h, 0.15]} />
        <meshStandardMaterial color="#fef3c7" emissive={baseColor} emissiveIntensity={emissive * 0.3} />
      </mesh>
      <mesh position={[-w/2, h/2, 0]} castShadow>
        <boxGeometry args={[0.15, h, d]} />
        <meshStandardMaterial color="#fef3c7" emissive={baseColor} emissiveIntensity={emissive * 0.3} />
      </mesh>
      <mesh position={[w/2, h/2, 0]} castShadow>
        <boxGeometry args={[0.15, h, d]} />
        <meshStandardMaterial color="#fef3c7" emissive={baseColor} emissiveIntensity={emissive * 0.3} />
      </mesh>
      <mesh position={[0, h + 1.5, 0]} rotation={[0, Math.PI/2, 0]} castShadow>
        <cylinderGeometry args={[d/2 + 0.5, d/2 + 0.5, w + 1, 3, 1, false, 0, Math.PI]} />
        <meshStandardMaterial color="#eab308" emissive={baseColor} emissiveIntensity={emissive} roughness={0.4} />
      </mesh>
      {[[-1,-1],[1,-1],[-1,1],[1,1]].map(([x,z]) => (
        <mesh key={`p-${x}-${z}`} position={[x * w/2, h/2, z * d/2]} castShadow>
          <cylinderGeometry args={[0.2, 0.2, h, 6]} />
          <meshStandardMaterial color="#4a4a4a" metalness={0.6} />
        </mesh>
      ))}
      <mesh position={[0, h + 4, 0]} castShadow>
        <cylinderGeometry args={[0.1, 0.1, 3, 6]} />
        <meshStandardMaterial color="#c0c0c0" metalness={0.7} />
      </mesh>
      <mesh position={[1, h + 4, 0]} castShadow>
        <planeGeometry args={[2, 1.2]} />
        <meshStandardMaterial color="#dc2626" side={THREE.DoubleSide} />
      </mesh>
    </group>
  );
}

// ---- 02. Banquet Tent (khemah_jamuan) -----------------------------
export function KhemahJamuan({ w, d, h, baseColor, roofColor, emissive }) {
  const walls = { '-z': [0, h/2, -d/2, w, h, 0.1], 'x': [w/2, h/2, 0, 0.1, h, d], '-x': [-w/2, h/2, 0, 0.1, h, d] };
  return (
    <group>
      <mesh position={[0, 0.1, 0]} receiveShadow>
        <boxGeometry args={[w, 0.2, d]} />
        <meshStandardMaterial color="#3d3d3d" />
      </mesh>
      {Object.entries(walls).map(([side, p]) => (
        <mesh key={side} position={[p[0], p[1], p[2]]} castShadow>
          <boxGeometry args={[p[3], p[4], p[5]]} />
          <meshStandardMaterial color={baseColor} transparent opacity={0.9} emissive={baseColor} emissiveIntensity={emissive * 0.3} />
        </mesh>
      ))}
      <mesh position={[0, h, 0]} castShadow>
        <sphereGeometry args={[Math.max(w, d) * 0.6, 12, 8, 0, Math.PI * 2, 0, Math.PI / 2]} />
        <meshStandardMaterial color={roofColor} emissive={baseColor} emissiveIntensity={emissive} roughness={0.4} />
      </mesh>
    </group>
  );
}

// ---- 03. Plant sales tent (khemah_jualan_tanaman) -----------------
export function KhemahJualanTanaman({ w, d, h, baseColor, roofColor, emissive }) {
  return (
    <group>
      <mesh position={[0, 0.1, 0]} receiveShadow>
        <boxGeometry args={[w, 0.2, d]} />
        <meshStandardMaterial color="#3d3d3d" />
      </mesh>
      <mesh position={[0, h/2, -d/2]} castShadow>
        <boxGeometry args={[w, h, 0.1]} />
        <meshStandardMaterial color={baseColor} transparent opacity={0.85} emissive={baseColor} emissiveIntensity={emissive * 0.3} />
      </mesh>
      <mesh position={[-w/2, h/2, 0]} castShadow>
        <boxGeometry args={[0.1, h, d]} />
        <meshStandardMaterial color={baseColor} transparent opacity={0.85} />
      </mesh>
      <mesh position={[w/2, h/2, 0]} castShadow>
        <boxGeometry args={[0.1, h, d]} />
        <meshStandardMaterial color={baseColor} transparent opacity={0.85} />
      </mesh>
      <mesh position={[0, h + 0.3, 0]} rotation={[Math.PI / 8, 0, 0]} castShadow>
        <boxGeometry args={[w + 0.5, 0.15, d + 1]} />
        <meshStandardMaterial color={roofColor} emissive={baseColor} emissiveIntensity={emissive} />
      </mesh>
      {[[-1,-1],[1,-1],[-1,1],[1,1]].map(([x,z]) => (
        <mesh key={`p-${x}-${z}`} position={[x * w/2, h/2, z * d/2]} castShadow>
          <cylinderGeometry args={[0.1, 0.1, h, 6]} />
          <meshStandardMaterial color="#2c3e50" metalness={0.5} />
        </mesh>
      ))}
      {[[-0.3, 0.3],[0.3, 0.3]].map(([px, pz]) => (
        <mesh key={`f-${px}-${pz}`} position={[px * w, 0.5, pz * d]} castShadow>
          <sphereGeometry args={[0.4, 8, 8]} />
          <meshStandardMaterial color="#16a34a" />
        </mesh>
      ))}
    </group>
  );
}

// ---- 04. Food tent (khemah_makan) ---------------------------------
export function KhemahMakan({ w, d, h, baseColor, roofColor, emissive }) {
  return (
    <group>
      <mesh position={[0, 0.1, 0]} receiveShadow>
        <boxGeometry args={[w, 0.2, d]} />
        <meshStandardMaterial color="#3d3d3d" />
      </mesh>
      <mesh position={[0, h/2, -d/2]} castShadow>
        <boxGeometry args={[w, h, 0.1]} />
        <meshStandardMaterial color={baseColor} transparent opacity={0.9} emissive={baseColor} emissiveIntensity={emissive * 0.3} />
      </mesh>
      <mesh position={[-w/2, h/2, 0]} castShadow>
        <boxGeometry args={[0.1, h, d]} />
        <meshStandardMaterial color={baseColor} transparent opacity={0.9} />
      </mesh>
      <mesh position={[w/2, h/2, 0]} castShadow>
        <boxGeometry args={[0.1, h, d]} />
        <meshStandardMaterial color={baseColor} transparent opacity={0.9} />
      </mesh>
      <mesh position={[0, h + 1, 0]} castShadow>
        <coneGeometry args={[Math.max(w, d) * 0.75, 2, 4]} />
        <meshStandardMaterial color={roofColor} emissive={baseColor} emissiveIntensity={emissive} />
      </mesh>
      <mesh position={[w * 0.3, h + 2, 0]} castShadow>
        <cylinderGeometry args={[0.2, 0.2, 1.5, 6]} />
        <meshStandardMaterial color="#4a4a4a" metalness={0.7} />
      </mesh>
    </group>
  );
}

// ---- 05. Support tents (closed rectangle w/ door) -----------------
export function SupportTent({ w, d, h, baseColor, roofColor, emissive }) {
  return (
    <group>
      <mesh position={[0, 0.1, 0]} receiveShadow>
        <boxGeometry args={[w, 0.2, d]} />
        <meshStandardMaterial color="#3d3d3d" />
      </mesh>
      <mesh position={[0, h/2, 0]} castShadow>
        <boxGeometry args={[w, h, d]} />
        <meshStandardMaterial color={baseColor} transparent opacity={0.9} emissive={baseColor} emissiveIntensity={emissive * 0.3} />
      </mesh>
      <mesh position={[0, h + 0.15, 0]} castShadow>
        <boxGeometry args={[w + 0.4, 0.3, d + 0.4]} />
        <meshStandardMaterial color={roofColor} emissive={baseColor} emissiveIntensity={emissive} />
      </mesh>
      <mesh position={[0, h/2 - 0.3, d/2 + 0.05]}>
        <boxGeometry args={[1, 1.8, 0.05]} />
        <meshStandardMaterial color="#1e293b" />
      </mesh>
    </group>
  );
}

// ---- 06. RAHMAH Madani discount tent ------------------------------
export function KhemahRahmah({ w, d, h, baseColor, roofColor, emissive }) {
  return (
    <group>
      <mesh position={[0, 0.1, 0]} receiveShadow>
        <boxGeometry args={[w, 0.2, d]} />
        <meshStandardMaterial color="#3d3d3d" />
      </mesh>
      <mesh position={[0, h / 2, -d / 2]} castShadow>
        <boxGeometry args={[w, h, 0.1]} />
        <meshStandardMaterial color="#fef3c7" emissive={baseColor} emissiveIntensity={emissive * 0.3} />
      </mesh>
      <mesh position={[-w / 2, h / 2, 0]} castShadow>
        <boxGeometry args={[0.1, h, d]} />
        <meshStandardMaterial color="#fef3c7" />
      </mesh>
      <mesh position={[w / 2, h / 2, 0]} castShadow>
        <boxGeometry args={[0.1, h, d]} />
        <meshStandardMaterial color="#fef3c7" />
      </mesh>
      <mesh position={[0, h + 0.5, d / 2 - 0.05]} castShadow>
        <boxGeometry args={[w, 1.4, 0.15]} />
        <meshStandardMaterial color="#f59e0b" emissive="#f59e0b" emissiveIntensity={0.4} />
      </mesh>
      <Html zIndexRange={[10, 0]} position={[0, h + 0.5, d / 2 + 0.05]} center distanceFactor={20} occlude={false}>
        <div className="text-black font-black text-xs tracking-widest pointer-events-none select-none">
          RAHMAH MADANI
        </div>
      </Html>
      <mesh position={[0, h + 0.3, 0]} rotation={[Math.PI / 12, 0, 0]} castShadow>
        <boxGeometry args={[w + 0.4, 0.15, d + 1]} />
        <meshStandardMaterial color={roofColor} emissive={baseColor} emissiveIntensity={emissive} />
      </mesh>
      {[[-1, -1], [1, -1], [-1, 1], [1, 1]].map(([x, z]) => (
        <mesh key={`p-${x}-${z}`} position={[x * w / 2, h / 2, z * d / 2]} castShadow>
          <cylinderGeometry args={[0.1, 0.1, h, 6]} />
          <meshStandardMaterial color="#78350f" />
        </mesh>
      ))}
      <mesh position={[0, 0.6, 0]}>
        <boxGeometry args={[w * 0.75, 0.1, d * 0.35]} />
        <meshStandardMaterial color="#ea580c" />
      </mesh>
    </group>
  );
}

// ---- 07. Small kiosk booth (10x10 ft) -----------------------------
export function SmallBooth({ w, d, h, baseColor, roofColor, emissive }) {
  return (
    <group>
      <mesh position={[0, 0.1, 0]} receiveShadow>
        <boxGeometry args={[w, 0.2, d]} />
        <meshStandardMaterial color="#4a4a4a" />
      </mesh>
      <mesh position={[0, h / 2, -d / 2]} castShadow>
        <boxGeometry args={[w, h, 0.1]} />
        <meshStandardMaterial color={baseColor} emissive={baseColor} emissiveIntensity={emissive * 0.3} />
      </mesh>
      <mesh position={[-w / 2, h / 2, 0]} castShadow>
        <boxGeometry args={[0.1, h, d]} />
        <meshStandardMaterial color={baseColor} />
      </mesh>
      <mesh position={[w / 2, h / 2, 0]} castShadow>
        <boxGeometry args={[0.1, h, d]} />
        <meshStandardMaterial color={baseColor} />
      </mesh>
      <mesh position={[0, h + 0.15, 0]} castShadow>
        <boxGeometry args={[w + 0.3, 0.2, d + 0.3]} />
        <meshStandardMaterial color={roofColor} emissive={baseColor} emissiveIntensity={emissive * 0.4} />
      </mesh>
      <mesh position={[0, h * 0.35, d / 2 - 0.05]}>
        <boxGeometry args={[w * 0.7, 0.1, 0.1]} />
        <meshStandardMaterial color="#e2e8f0" />
      </mesh>
      <mesh position={[0, h * 0.85, d / 2]}>
        <boxGeometry args={[w * 0.85, 0.35, 0.05]} />
        <meshStandardMaterial color="#fbbf24" />
      </mesh>
    </group>
  );
}

// ---- 08. Default exhibitor tent (khemah_pempamer) -----------------
export function KhemahPempamer({ w, d, h, baseColor, roofColor, emissive }) {
  return (
    <group>
      <mesh position={[0, 0.1, 0]} receiveShadow>
        <boxGeometry args={[w, 0.2, d]} />
        <meshStandardMaterial color="#3d3d3d" />
      </mesh>
      {[[-1,-1],[1,-1],[-1,1],[1,1]].map(([x,z]) => (
        <mesh key={`d-${x}-${z}`} position={[x * w/2, h/2, z * d/2]} castShadow>
          <cylinderGeometry args={[0.1, 0.1, h, 6]} />
          <meshStandardMaterial color="#2c3e50" metalness={0.5} />
        </mesh>
      ))}
      <mesh position={[0, h/2, -d/2 + 0.05]}>
        <boxGeometry args={[w, h, 0.1]} />
        <meshStandardMaterial color={baseColor} transparent opacity={0.9} emissive={baseColor} emissiveIntensity={emissive * 0.3} />
      </mesh>
      <mesh position={[-w/2 + 0.05, h/2, 0]}>
        <boxGeometry args={[0.1, h, d]} />
        <meshStandardMaterial color={baseColor} transparent opacity={0.9} />
      </mesh>
      <mesh position={[w/2 - 0.05, h/2, 0]}>
        <boxGeometry args={[0.1, h, d]} />
        <meshStandardMaterial color={baseColor} transparent opacity={0.9} />
      </mesh>
      <mesh position={[0, h + 1, 0]} castShadow>
        <coneGeometry args={[Math.max(w, d) * 0.75, 2, 4]} />
        <meshStandardMaterial color={roofColor} emissive={baseColor} emissiveIntensity={emissive} />
      </mesh>
    </group>
  );
}
